/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getPatientProcedures } from 'app/entities/patient-procedure/patient-procedure.reducer';
import { getEntities as getDoctors } from 'app/entities/doctor/doctor.reducer';
import { getEntities as getVitals } from 'app/entities/vitals/vitals.reducer';
import { getEntities as getLabPrescriptions } from 'app/entities/lab-prescription/lab-prescription.reducer';
import { Thyroid } from 'app/shared/model/enumerations/thyroid.model';
import { TypeOfAnaesthesia } from 'app/shared/model/enumerations/type-of-anaesthesia.model';
import { ASACategory } from 'app/shared/model/enumerations/asa-category.model';
import { createEntity, getEntity, reset, updateEntity } from './pre-op-assessment.reducer';

export const PreOpAssessmentUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const patientProcedures = useAppSelector(state => state.patientProcedure.entities);
  const doctors = useAppSelector(state => state.doctor.entities);
  const vitals = useAppSelector(state => state.vitals.entities);
  const labPrescriptions = useAppSelector(state => state.labPrescription.entities);
  const preOpAssessmentEntity = useAppSelector(state => state.preOpAssessment.entity);
  const loading = useAppSelector(state => state.preOpAssessment.loading);
  const updating = useAppSelector(state => state.preOpAssessment.updating);
  const updateSuccess = useAppSelector(state => state.preOpAssessment.updateSuccess);
  const thyroidValues = Object.keys(Thyroid);
  const typeOfAnaesthesiaValues = Object.keys(TypeOfAnaesthesia);
  const aSACategoryValues = Object.keys(ASACategory);

  const handleClose = () => {
    navigate(`/pre-op-assessment${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getPatientProcedures({}));
    dispatch(getDoctors({}));
    dispatch(getVitals({}));
    dispatch(getLabPrescriptions({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.surgeryDateTime !== undefined && typeof values.surgeryDateTime !== 'number') {
      values.surgeryDateTime = Number(values.surgeryDateTime);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }

    const entity = {
      ...preOpAssessmentEntity,
      ...values,
      surgery: patientProcedures.find(it => it.id.toString() === values.surgery?.toString()),
      anaesthesiologist: doctors.find(it => it.id.toString() === values.anaesthesiologist?.toString()),
      vitals: vitals.find(it => it.id.toString() === values.vitals?.toString()),
      labPres: labPrescriptions.find(it => it.id.toString() === values.labPres?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          thyroid: 'NONE',
          typeOfAnaesthesia: 'GA',
          aSaCategory: 'ASA_I',
          ...preOpAssessmentEntity,
          surgery: preOpAssessmentEntity?.surgery?.id,
          anaesthesiologist: preOpAssessmentEntity?.anaesthesiologist?.id,
          vitals: preOpAssessmentEntity?.vitals?.id,
          labPres: preOpAssessmentEntity?.labPres?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.preOpAssessment.home.createOrEditLabel" data-cy="PreOpAssessmentCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.preOpAssessment.home.createOrEditLabel">Create or edit a PreOpAssessment</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="pre-op-assessment-id"
                  label={translate('tinyMedicRestApp.preOpAssessment.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.surgeryDateTime')}
                id="pre-op-assessment-surgeryDateTime"
                name="surgeryDateTime"
                data-cy="surgeryDateTime"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.hypertension')}
                id="pre-op-assessment-hypertension"
                name="hypertension"
                data-cy="hypertension"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.diabetes')}
                id="pre-op-assessment-diabetes"
                name="diabetes"
                data-cy="diabetes"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.asthma')}
                id="pre-op-assessment-asthma"
                name="asthma"
                data-cy="asthma"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.allergy')}
                id="pre-op-assessment-allergy"
                name="allergy"
                data-cy="allergy"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.epilepsy')}
                id="pre-op-assessment-epilepsy"
                name="epilepsy"
                data-cy="epilepsy"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.ihd')}
                id="pre-op-assessment-ihd"
                name="ihd"
                data-cy="ihd"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.thyroid')}
                id="pre-op-assessment-thyroid"
                name="thyroid"
                data-cy="thyroid"
                type="select"
              >
                {thyroidValues.map(thyroid => (
                  <option value={thyroid} key={thyroid}>
                    {translate(`tinyMedicRestApp.Thyroid.${thyroid}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.previousSurgeyInfo')}
                id="pre-op-assessment-previousSurgeyInfo"
                name="previousSurgeyInfo"
                data-cy="previousSurgeyInfo"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.historyOfTB')}
                id="pre-op-assessment-historyOfTB"
                name="historyOfTB"
                data-cy="historyOfTB"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.otherHistory')}
                id="pre-op-assessment-otherHistory"
                name="otherHistory"
                data-cy="otherHistory"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.icterus')}
                id="pre-op-assessment-icterus"
                name="icterus"
                data-cy="icterus"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.pallor')}
                id="pre-op-assessment-pallor"
                name="pallor"
                data-cy="pallor"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.lymphadenopathy')}
                id="pre-op-assessment-lymphadenopathy"
                name="lymphadenopathy"
                data-cy="lymphadenopathy"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.edema')}
                id="pre-op-assessment-edema"
                name="edema"
                data-cy="edema"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.airwayDentition')}
                id="pre-op-assessment-airwayDentition"
                name="airwayDentition"
                data-cy="airwayDentition"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.thyroidText')}
                id="pre-op-assessment-thyroidText"
                name="thyroidText"
                data-cy="thyroidText"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.spine')}
                id="pre-op-assessment-spine"
                name="spine"
                data-cy="spine"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.mallampatiGrade')}
                id="pre-op-assessment-mallampatiGrade"
                name="mallampatiGrade"
                data-cy="mallampatiGrade"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.tmDistance')}
                id="pre-op-assessment-tmDistance"
                name="tmDistance"
                data-cy="tmDistance"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.cvs')}
                id="pre-op-assessment-cvs"
                name="cvs"
                data-cy="cvs"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.respiratorySystem')}
                id="pre-op-assessment-respiratorySystem"
                name="respiratorySystem"
                data-cy="respiratorySystem"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.git')}
                id="pre-op-assessment-git"
                name="git"
                data-cy="git"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.cns')}
                id="pre-op-assessment-cns"
                name="cns"
                data-cy="cns"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.otherFindings')}
                id="pre-op-assessment-otherFindings"
                name="otherFindings"
                data-cy="otherFindings"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.psychologicalNeeds')}
                id="pre-op-assessment-psychologicalNeeds"
                name="psychologicalNeeds"
                data-cy="psychologicalNeeds"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.socialNeeds')}
                id="pre-op-assessment-socialNeeds"
                name="socialNeeds"
                data-cy="socialNeeds"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.economicNeeds')}
                id="pre-op-assessment-economicNeeds"
                name="economicNeeds"
                data-cy="economicNeeds"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.otherInvestigations')}
                id="pre-op-assessment-otherInvestigations"
                name="otherInvestigations"
                data-cy="otherInvestigations"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.dischargeNeeds')}
                id="pre-op-assessment-dischargeNeeds"
                name="dischargeNeeds"
                data-cy="dischargeNeeds"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.ecg')}
                id="pre-op-assessment-ecg"
                name="ecg"
                data-cy="ecg"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.echo')}
                id="pre-op-assessment-echo"
                name="echo"
                data-cy="echo"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.chestXRay')}
                id="pre-op-assessment-chestXRay"
                name="chestXRay"
                data-cy="chestXRay"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.typeOfAnaesthesia')}
                id="pre-op-assessment-typeOfAnaesthesia"
                name="typeOfAnaesthesia"
                data-cy="typeOfAnaesthesia"
                type="select"
              >
                {typeOfAnaesthesiaValues.map(typeOfAnaesthesia => (
                  <option value={typeOfAnaesthesia} key={typeOfAnaesthesia}>
                    {translate(`tinyMedicRestApp.TypeOfAnaesthesia.${typeOfAnaesthesia}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.aSaCategory')}
                id="pre-op-assessment-aSaCategory"
                name="aSaCategory"
                data-cy="aSaCategory"
                type="select"
              >
                {aSACategoryValues.map(aSACategory => (
                  <option value={aSACategory} key={aSACategory}>
                    {translate(`tinyMedicRestApp.ASACategory.${aSACategory}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.active')}
                id="pre-op-assessment-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.cursor')}
                id="pre-op-assessment-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.lastupdated')}
                id="pre-op-assessment-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.microSec')}
                id="pre-op-assessment-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.timestampentry')}
                id="pre-op-assessment-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.preOpAssessment.testsIgnored')}
                id="pre-op-assessment-testsIgnored"
                name="testsIgnored"
                data-cy="testsIgnored"
                check
                type="checkbox"
              />
              <ValidatedField
                id="pre-op-assessment-surgery"
                name="surgery"
                data-cy="surgery"
                label={translate('tinyMedicRestApp.preOpAssessment.surgery')}
                type="select"
                required
              >
                <option value="" key="0" />
                {patientProcedures
                  ? patientProcedures.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="pre-op-assessment-anaesthesiologist"
                name="anaesthesiologist"
                data-cy="anaesthesiologist"
                label={translate('tinyMedicRestApp.preOpAssessment.anaesthesiologist')}
                type="select"
                required
              >
                <option value="" key="0" />
                {doctors
                  ? doctors.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="pre-op-assessment-vitals"
                name="vitals"
                data-cy="vitals"
                label={translate('tinyMedicRestApp.preOpAssessment.vitals')}
                type="select"
                required
              >
                <option value="" key="0" />
                {vitals
                  ? vitals.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="pre-op-assessment-labPres"
                name="labPres"
                data-cy="labPres"
                label={translate('tinyMedicRestApp.preOpAssessment.labPres')}
                type="select"
              >
                <option value="" key="0" />
                {labPrescriptions
                  ? labPrescriptions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/pre-op-assessment" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PreOpAssessmentUpdate;
